import {
	ArrowRight,
	BarChart3,
	CheckCircle2,
	ListChecks,
	RotateCcw,
	ShieldAlert,
	Sparkles,
	Target,
} from "lucide-react";
import { useMemo, useState } from "react";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type DomainId =
	| "sleep"
	| "stress"
	| "action"
	| "self"
	| "relationship"
	| "body"
	| "order"
	| "positive"
	| "safety";

export type SituationId =
	| "sleep_onset"
	| "sleep_rumination"
	| "sleep_nightwake"
	| "sleep_tired"
	| "sleep_rhythm"
	| "sleep_phone"
	| "stress_anxiety"
	| "stress_overload"
	| "stress_irritable"
	| "low_mood"
	| "panic_signal"
	| "action_procrastination"
	| "action_low_motivation"
	| "action_unclear_step"
	| "perfection_block"
	| "self_criticism"
	| "self_low_confidence"
	| "relationship_lonely"
	| "relationship_conflict"
	| "body_fatigue"
	| "body_tension"
	| "order_chaos"
	| "phone_overuse"
	| "positive_low_joy"
	| "safety_risk";

export type PracticeId =
	| "sleep-breath"
	| "body-scan"
	| "worry-dump"
	| "tomorrow-first-step"
	| "lights-down"
	| "fixed-wake"
	| "phone-away"
	| "white-noise"
	| "box-breath"
	| "grounding-54321"
	| "emotion-name"
	| "control-circle"
	| "one-minute-start"
	| "single-priority"
	| "done-list"
	| "self-kind-sentence"
	| "message-one"
	| "boundary-sentence"
	| "short-walk"
	| "water"
	| "tidy-five"
	| "gratitude-one"
	| "safety-contact"
	| "professional-help";

export type ScoreMap<T extends string> = Partial<Record<T, number>>;

export type AnswerOption = {
	id: string;
	label: string;
	description?: string;
	domainScores?: ScoreMap<DomainId>;
	situationScores?: ScoreMap<SituationId>;
	excludeSituations?: SituationId[];
};

export type Question = {
	id: string;
	title: string;
	helper: string;
	type: "single" | "multi";
	maxSelect?: number;
	priority: number;
	targetDomains?: DomainId[];
	targetSituations?: SituationId[];
	options: AnswerOption[];
	shouldAsk?: (state: AssessmentState) => boolean;
};

export type AnswerRecord = {
	questionId: string;
	questionTitle: string;
	options: AnswerOption[];
};

export type AssessmentState = {
	answers: AnswerRecord[];
	domainScores: Record<DomainId, number>;
	situationScores: Record<SituationId, number>;
	excludedSituations: Set<SituationId>;
};

export type Practice = {
	title: string;
	category: string;
	duration: string;
	mode: "认知" | "身体" | "行动" | "关系" | "积极" | "记录" | "安全";
	reason: string;
	theory: string;
	positiveFit: "是" | "部分是" | "不是";
	evidenceLevel: "较强" | "中等" | "探索" | "安全优先";
	selectionReason: string;
	notOtherReason: string;
	betterAlternative: string;
};

export type Situation = {
	id: SituationId;
	name: string;
	domain: DomainId;
	description: string;
	practiceIds: PracticeId[];
};

export const MAX_QUESTIONS = 10;
export const MIN_QUESTIONS = 4;

export const domains: Record<DomainId, { name: string; description: string }> = {
	sleep: { name: "睡眠与恢复", description: "入睡、睡眠维持、恢复感、作息节律" },
	stress: { name: "情绪与压力", description: "焦虑、压力、烦躁、低落和身体警报" },
	action: { name: "行动与动力", description: "拖延、启动困难、低动力和第一步不清" },
	self: { name: "自我关系", description: "自责、低自信、完美主义和不允许休息" },
	relationship: { name: "人际关系", description: "孤独、冲突、表达需求和边界" },
	body: { name: "身体能量", description: "疲惫、紧绷、身体照顾和活动不足" },
	order: { name: "生活秩序", description: "手机、空间、计划和习惯秩序" },
	positive: { name: "积极生活", description: "愉悦、感恩、意义和正向体验" },
	safety: { name: "安全风险", description: "强烈绝望、自伤风险和专业支持" },
};

export const situations: Record<SituationId, Situation> = {
	sleep_onset: {
		id: "sleep_onset",
		name: "入睡难",
		domain: "sleep",
		description: "躺下后较长时间睡不着，常伴随紧张、担心或身体没有降下来。",
		practiceIds: ["sleep-breath", "body-scan", "worry-dump", "tomorrow-first-step"],
	},
	sleep_rumination: {
		id: "sleep_rumination",
		name: "睡前想太多",
		domain: "sleep",
		description: "睡前反复想明天、工作、人际或未完成的事，大脑停不下来。",
		practiceIds: ["worry-dump", "control-circle", "tomorrow-first-step", "sleep-breath"],
	},
	sleep_nightwake: {
		id: "sleep_nightwake",
		name: "夜醒 / 浅睡",
		domain: "sleep",
		description: "容易醒、睡眠浅，醒后难以再次入睡。",
		practiceIds: ["body-scan", "white-noise", "sleep-breath", "lights-down"],
	},
	sleep_tired: {
		id: "sleep_tired",
		name: "醒来仍疲惫",
		domain: "sleep",
		description: "睡够时长但恢复感差，通常要同时看压力、身体能量和作息规律。",
		practiceIds: ["fixed-wake", "water", "short-walk", "done-list"],
	},
	sleep_rhythm: {
		id: "sleep_rhythm",
		name: "作息乱",
		domain: "sleep",
		description: "睡觉和起床时间漂移，常被手机、任务、情绪或白天补觉打乱。",
		practiceIds: ["fixed-wake", "phone-away", "lights-down", "single-priority"],
	},
	sleep_phone: {
		id: "sleep_phone",
		name: "睡前刷手机停不下",
		domain: "sleep",
		description: "知道该睡，但靠手机逃避压力、孤独或未完成任务。",
		practiceIds: ["phone-away", "lights-down", "tomorrow-first-step", "white-noise"],
	},
	stress_anxiety: {
		id: "stress_anxiety",
		name: "焦虑担心",
		domain: "stress",
		description: "对未来、结果或不确定性反复担心，难以停下来。",
		practiceIds: ["box-breath", "control-circle", "emotion-name", "worry-dump"],
	},
	stress_overload: {
		id: "stress_overload",
		name: "压力过载",
		domain: "stress",
		description: "任务太多、脑子拥挤，感觉被事情推着走。",
		practiceIds: ["single-priority", "control-circle", "short-walk", "one-minute-start"],
	},
	stress_irritable: {
		id: "stress_irritable",
		name: "烦躁易怒",
		domain: "stress",
		description: "容易被小事点燃，身体和情绪都处在高唤醒状态。",
		practiceIds: ["grounding-54321", "box-breath", "short-walk", "emotion-name"],
	},
	low_mood: {
		id: "low_mood",
		name: "情绪低落",
		domain: "stress",
		description: "开心感降低，对事情提不起兴趣，容易沉下去。",
		practiceIds: ["done-list", "gratitude-one", "short-walk", "message-one"],
	},
	panic_signal: {
		id: "panic_signal",
		name: "身体警报感",
		domain: "stress",
		description: "心跳、胸闷、呼吸急或强烈害怕，身体像进入警报状态。",
		practiceIds: ["grounding-54321", "box-breath", "body-scan", "professional-help"],
	},
	action_procrastination: {
		id: "action_procrastination",
		name: "拖延失控",
		domain: "action",
		description: "知道要做但一直躲开，常和压力、害怕做不好或手机有关。",
		practiceIds: ["one-minute-start", "single-priority", "phone-away", "tidy-five"],
	},
	action_low_motivation: {
		id: "action_low_motivation",
		name: "低动力",
		domain: "action",
		description: "不是不会做，而是启动能量很低，缺少开始的推力。",
		practiceIds: ["water", "short-walk", "one-minute-start", "done-list"],
	},
	action_unclear_step: {
		id: "action_unclear_step",
		name: "第一步不清",
		domain: "action",
		description: "目标太大或太模糊，不知道从哪里开始。",
		practiceIds: ["tomorrow-first-step", "single-priority", "one-minute-start"],
	},
	perfection_block: {
		id: "perfection_block",
		name: "怕做不好",
		domain: "action",
		description: "因为怕结果不好、怕被评价，所以迟迟不开始。",
		practiceIds: ["one-minute-start", "self-kind-sentence", "control-circle"],
	},
	self_criticism: {
		id: "self_criticism",
		name: "自责内耗",
		domain: "self",
		description: "经常责怪自己，把问题解释成“我不够好”。",
		practiceIds: ["self-kind-sentence", "emotion-name", "done-list"],
	},
	self_low_confidence: {
		id: "self_low_confidence",
		name: "缺乏自信",
		domain: "self",
		description: "低估自己的能力，不敢表达或行动。",
		practiceIds: ["done-list", "self-kind-sentence", "one-minute-start"],
	},
	relationship_lonely: {
		id: "relationship_lonely",
		name: "孤独断联",
		domain: "relationship",
		description: "想连接但不知道找谁，或觉得没人理解自己。",
		practiceIds: ["message-one", "gratitude-one", "emotion-name"],
	},
	relationship_conflict: {
		id: "relationship_conflict",
		name: "关系冲突",
		domain: "relationship",
		description: "和他人冲突后反复想、委屈、愤怒或不知道怎么说。",
		practiceIds: ["boundary-sentence", "emotion-name", "control-circle"],
	},
	body_fatigue: {
		id: "body_fatigue",
		name: "身体疲惫",
		domain: "body",
		description: "身体能量低，基础照顾和恢复不足。",
		practiceIds: ["water", "short-walk", "body-scan", "done-list"],
	},
	body_tension: {
		id: "body_tension",
		name: "身体紧绷",
		domain: "body",
		description: "肩颈、胸口、胃或呼吸明显紧，身体处在防御状态。",
		practiceIds: ["body-scan", "box-breath", "short-walk"],
	},
	order_chaos: {
		id: "order_chaos",
		name: "生活秩序乱",
		domain: "order",
		description: "空间、计划、作息或任务秩序混乱，消耗掌控感。",
		practiceIds: ["tidy-five", "single-priority", "fixed-wake"],
	},
	phone_overuse: {
		id: "phone_overuse",
		name: "手机停不下",
		domain: "order",
		description: "用手机逃避压力、无聊、孤独或不想面对的任务。",
		practiceIds: ["phone-away", "single-priority", "tomorrow-first-step"],
	},
	positive_low_joy: {
		id: "positive_low_joy",
		name: "缺少正向体验",
		domain: "positive",
		description: "生活里缺少愉悦、感恩、期待和意义感。",
		practiceIds: ["gratitude-one", "done-list", "message-one"],
	},
	safety_risk: {
		id: "safety_risk",
		name: "需要安全支持",
		domain: "safety",
		description: "出现明确自伤念头、失控风险或强烈绝望时，优先转向真人和专业支持。",
		practiceIds: ["safety-contact", "professional-help"],
	},
};

export const practiceCatalog: Record<PracticeId, Practice> = {
	"sleep-breath": {
		title: "睡前延长呼气",
		category: "睡眠修复",
		duration: "2 分钟",
		mode: "身体",
		reason: "先降低身体唤醒，让入睡阻力变小。",
		theory: "正念呼吸 / 放松训练 / 睡前身体降唤醒",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合入睡难、紧张、心跳快这类身体高唤醒状态，成本低，2 分钟能开始。",
		notOtherReason: "此时先讲道理或做感恩练习，可能压不住身体警报；先让身体降下来更稳。",
		betterAlternative: "如果用户是严重长期失眠，更好的方案是 CBT-I 课程或专业睡眠评估。",
	},
	"body-scan": {
		title: "快速身体扫描",
		category: "身体调节",
		duration: "2-4 分钟",
		mode: "身体",
		reason: "把注意力从想法拉回身体，识别并松开紧绷。",
		theory: "正念身体扫描 / 躯体觉察",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合焦虑、入睡难、身体紧绷，因为它直接训练身体觉察和放松。",
		notOtherReason:
			"如果用户没有明显身体紧张，身体扫描不一定是最短路径，认知整理或行动启动可能更合适。",
		betterAlternative: "如果用户出现强烈惊恐，先用 5-4-3-2-1 稳定练习会更具体。",
	},
	"worry-dump": {
		title: "写下所有担忧",
		category: "认知整理",
		duration: "3 分钟",
		mode: "认知",
		reason: "把脑内循环转移到纸面，降低睡前反刍。",
		theory: "CBT 认知外化 / 表达性书写",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合睡前想太多、焦虑担心、未完成感，把循环想法先放到外部载体。",
		notOtherReason: "直接冥想可能会让反刍更明显；先写出来，再做放松更容易。",
		betterAlternative: "如果担忧已经很清楚，更好的下一步是可控/不可控分类或明天第一步。",
	},
	"tomorrow-first-step": {
		title: "写下明天第一步",
		category: "行动启动",
		duration: "1 分钟",
		mode: "行动",
		reason: "把未完成感变成一个可执行动作。",
		theory: "行为激活 / 执行意图 / CBT 问题解决",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合未完成任务导致的焦虑、睡前反刍和拖延，能把抽象压力变成下一步。",
		notOtherReason: "如果用户处在强身体唤醒，先做呼吸比规划更合适；否则规划可能继续激活大脑。",
		betterAlternative: "如果任务很多，先做唯一重点；如果怕做不好，先做 1 分钟启动。",
	},
	"lights-down": {
		title: "睡前 30 分钟调暗灯光",
		category: "睡眠修复",
		duration: "1 分钟设置",
		mode: "行动",
		reason: "给身体一个进入夜间模式的环境信号。",
		theory: "睡眠卫生 / CBT-I 刺激控制",
		positiveFit: "不是",
		evidenceLevel: "较强",
		selectionReason: "适合作息乱、睡前手机、入睡困难，先改变环境线索，比要求用户立刻睡着更可执行。",
		notOtherReason: "单纯呼吸无法解决光照和手机刺激；环境刺激还在，放松效果会被抵消。",
		betterAlternative: "如果用户主要是晚睡刷手机，更好的主动作是手机放远，再配合调暗灯光。",
	},
	"fixed-wake": {
		title: "设置固定起床时间",
		category: "睡眠修复",
		duration: "1 分钟",
		mode: "行动",
		reason: "先固定起床锚点，比强行早睡更容易执行。",
		theory: "CBT-I / 昼夜节律稳定 / 行为锚点",
		positiveFit: "不是",
		evidenceLevel: "较强",
		selectionReason: "适合作息乱和醒来疲惫。固定起床比强迫早睡更容易形成节律。",
		notOtherReason: "如果只做睡前放松但起床时间漂移，节律仍然乱，恢复感很难稳定。",
		betterAlternative: "如果用户有倒班、严重失眠或长期昼夜颠倒，需要更完整的睡眠方案。",
	},
	"phone-away": {
		title: "手机放到够不到的地方",
		category: "生活秩序",
		duration: "1 分钟",
		mode: "行动",
		reason: "降低自动刷手机的启动概率。",
		theory: "行为设计 / 刺激控制 / 环境重构",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合睡前刷手机、手机逃避任务和注意力失控，直接减少触发源。",
		notOtherReason: "靠意志力通常不稳定；先改环境摩擦，比劝用户自控更现实。",
		betterAlternative: "如果手机是为了逃避焦虑，还要搭配担忧卸载或明天第一步。",
	},
	"white-noise": {
		title: "白噪音 / 环境音放松",
		category: "睡眠修复",
		duration: "5-20 分钟",
		mode: "身体",
		reason: "给浅睡和环境干扰一个低刺激背景。",
		theory: "睡眠环境管理 / 放松辅助",
		positiveFit: "不是",
		evidenceLevel: "探索",
		selectionReason: "适合浅睡、环境干扰、需要外部声音陪伴入睡的人，作为辅助动作。",
		notOtherReason: "它不能解决焦虑、作息乱或手机问题，只能作为低风险辅助。",
		betterAlternative: "如果用户是反刍导致睡不着，担忧卸载或呼吸练习更优先。",
	},
	"box-breath": {
		title: "方块呼吸",
		category: "呼吸放松",
		duration: "2 分钟",
		mode: "身体",
		reason: "用节律呼吸降低压力反应。",
		theory: "呼吸调节 / 正念稳定",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合焦虑、压力强、烦躁时快速把注意力和呼吸拉回可控节律。",
		notOtherReason: "高压力时直接做积极记录可能太跳跃；先稳定身体更容易接受。",
		betterAlternative: "如果用户呼吸练习会不舒服，改用 5-4-3-2-1 或短距离走一圈。",
	},
	"grounding-54321": {
		title: "5-4-3-2-1 稳定练习",
		category: "身体调节",
		duration: "3 分钟",
		mode: "身体",
		reason: "强情绪或警报感时，先把注意力带回当下。",
		theory: "创伤知情稳定技术 / 正念接地",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合惊恐、强焦虑、烦躁失控，先让用户回到当下环境。",
		notOtherReason: "此时不适合深挖原因或长篇反思，可能越想越乱。",
		betterAlternative: "如果出现明确自伤或失控风险，应优先真人支持，而不是自助练习。",
	},
	"emotion-name": {
		title: "给当前状态命名",
		category: "情绪觉察",
		duration: "1 分钟",
		mode: "记录",
		reason: "先识别状态，避免被情绪直接推着走。",
		theory: "情绪标记 / 正念觉察 / 情绪调节",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合情绪混乱、关系冲突、焦虑担心，先从混沌中分辨状态。",
		notOtherReason: "如果用户已经知道问题，继续命名价值有限，应进入行动或关系表达。",
		betterAlternative: "如果强度很高，先用接地或呼吸，再做情绪命名。",
	},
	"control-circle": {
		title: "可控 / 不可控分类",
		category: "认知整理",
		duration: "3 分钟",
		mode: "认知",
		reason: "把担心拆成能行动和暂时放下的两类。",
		theory: "CBT 认知重评 / ACT 控制圈",
		positiveFit: "不是",
		evidenceLevel: "中等",
		selectionReason: "适合焦虑担心、压力过载和关系冲突，把无边界担心拆成可处理范围。",
		notOtherReason: "如果用户只是低能量，分类会增加负担；先做身体或 1 分钟启动更好。",
		betterAlternative: "如果可控项已经明确，下一步应转成明天第一步或唯一重点。",
	},
	"one-minute-start": {
		title: "只做 1 分钟",
		category: "行动启动",
		duration: "1 分钟",
		mode: "行动",
		reason: "先绕过启动阻力，不要求一次完成。",
		theory: "行为激活 / 微习惯 / 启动阻力降低",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合拖延、低动力、怕做不好，用极小动作恢复行动感。",
		notOtherReason: "一开始就设大目标会提高失败感；1 分钟更适合轻训练。",
		betterAlternative: "如果用户不知道做什么，先写最小第一步；如果手机干扰，先手机放远。",
	},
	"single-priority": {
		title: "写下今天唯一重点",
		category: "任务管理",
		duration: "1 分钟",
		mode: "行动",
		reason: "压力过载时先减少选择数量。",
		theory: "问题解决训练 / 注意力管理 / 执行功能支持",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合压力过载、任务太多、计划失控，先减少决策负担。",
		notOtherReason: "如果直接让用户做很多计划，会继续过载；唯一重点更轻。",
		betterAlternative: "如果用户已经有明确任务但启动不了，改用只做 1 分钟。",
	},
	"done-list": {
		title: "列今天已完成 3 件事",
		category: "积极建构",
		duration: "2 分钟",
		mode: "积极",
		reason: "补回成就感和掌控感。",
		theory: "积极心理学成就觉察 / 行为激活",
		positiveFit: "是",
		evidenceLevel: "中等",
		selectionReason: "适合情绪低落、低动力、自责，把注意力从没做到转向已经做到。",
		notOtherReason: "低落时直接制定新目标可能压力大；先确认已有行动更温和。",
		betterAlternative: "如果用户没有任何完成感，先做喝水、走一圈这类可立即完成动作。",
	},
	"self-kind-sentence": {
		title: "给自己写一句温和的话",
		category: "自我慈悲",
		duration: "1 分钟",
		mode: "认知",
		reason: "降低自责带来的二次消耗。",
		theory: "自我慈悲 / CBT 自我对话调整",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合自责、低自信、怕做不好，先减少攻击自己的语言。",
		notOtherReason: "如果用户主要是任务混乱，仅写温和话不够，需要行动结构。",
		betterAlternative: "如果自责很深，后续需要完整自我慈悲课程，而不是一句话。",
	},
	"message-one": {
		title: "给一个人发一条消息",
		category: "关系连接",
		duration: "2 分钟",
		mode: "关系",
		reason: "用低成本动作恢复连接。",
		theory: "积极关系 / 社会支持 / 行为激活",
		positiveFit: "是",
		evidenceLevel: "中等",
		selectionReason: "适合孤独、低落、缺少正向体验，用一个低风险动作恢复连接。",
		notOtherReason: "如果当前是冲突或边界问题，直接联系可能加剧压力，应先写边界表达。",
		betterAlternative: "如果用户没有合适对象，改成写一封不发送的信或联系专业支持。",
	},
	"boundary-sentence": {
		title: "写一句边界表达",
		category: "关系连接",
		duration: "3 分钟",
		mode: "关系",
		reason: "冲突后先把需求表达成一句可说的话。",
		theory: "人际效能训练 / 非暴力沟通 / CBT 问题解决",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合关系冲突、不敢拒绝、需求说不清，先形成一句可执行表达。",
		notOtherReason: "如果用户只是孤独，边界表达不是主需求；连接动作更合适。",
		betterAlternative: "如果关系存在控制或暴力风险，应优先安全计划和外部支持。",
	},
	"short-walk": {
		title: "短距离走一圈",
		category: "身体活动",
		duration: "3-5 分钟",
		mode: "身体",
		reason: "低能量时先用身体动作启动状态。",
		theory: "行为激活 / 身体活动调节",
		positiveFit: "部分是",
		evidenceLevel: "中等",
		selectionReason: "适合低动力、压力过载、情绪低落，用身体动作打破静止状态。",
		notOtherReason: "如果用户很疲惫或身体不适，运动可能太难；先喝水或身体扫描。",
		betterAlternative: "如果用户能量更高，可以升级成 10 分钟散步或轻运动课程。",
	},
	water: {
		title: "喝水",
		category: "身体照顾",
		duration: "30 秒",
		mode: "身体",
		reason: "先补一个最小身体照顾动作。",
		theory: "行为激活 / 基础自我照顾",
		positiveFit: "不是",
		evidenceLevel: "探索",
		selectionReason: "适合非常低能量、照顾断掉时，先做一个几乎不会失败的动作。",
		notOtherReason: "它不是心理训练核心动作，只是最低启动动作，不能替代情绪训练。",
		betterAlternative: "如果用户状态允许，短距离走一圈或列已完成事项更有训练价值。",
	},
	"tidy-five": {
		title: "整理空间 5 分钟",
		category: "生活秩序",
		duration: "5 分钟",
		mode: "行动",
		reason: "用环境秩序恢复一点掌控感。",
		theory: "行为激活 / 环境重构 / 掌控感训练",
		positiveFit: "部分是",
		evidenceLevel: "探索",
		selectionReason: "适合生活秩序乱、拖延、低掌控感，用外部环境带动内部秩序。",
		notOtherReason: "如果用户主要是焦虑担心，整理空间可能只是回避，需搭配认知整理。",
		betterAlternative: "如果空间太乱，先把一件物品归位，比 5 分钟整理更低门槛。",
	},
	"gratitude-one": {
		title: "写下一件感恩的事",
		category: "感恩积极",
		duration: "1 分钟",
		mode: "积极",
		reason: "训练注意力看到仍然存在的支持和资源。",
		theory: "积极心理学感恩练习",
		positiveFit: "是",
		evidenceLevel: "较强",
		selectionReason: "适合缺少正向体验、低落但仍有可感知资源时，训练正向注意力。",
		notOtherReason: "强烈痛苦时强行感恩会显得无效甚至冒犯，应先稳定情绪。",
		betterAlternative: "如果用户抗拒感恩，改成记录一个小确幸或完成 3 件事。",
	},
	"safety-contact": {
		title: "联系可信任的人",
		category: "安全支持",
		duration: "立即",
		mode: "安全",
		reason: "风险信号出现时，不能只靠自助练习。",
		theory: "危机干预 / 社会支持",
		positiveFit: "不是",
		evidenceLevel: "安全优先",
		selectionReason: "适合自伤、失控、强烈绝望风险，自助练习不能作为主方案。",
		notOtherReason: "此时推荐感恩、呼吸或打卡都不够负责任，必须先连接真人支持。",
		betterAlternative: "如果有立即危险，应联系当地紧急服务或专业危机热线。",
	},
	"professional-help": {
		title: "联系专业帮助或当地紧急支持",
		category: "安全支持",
		duration: "立即",
		mode: "安全",
		reason: "强烈风险需要真人和专业系统介入。",
		theory: "危机干预 / 专业转介",
		positiveFit: "不是",
		evidenceLevel: "安全优先",
		selectionReason: "适合明确风险、长期严重症状或自助无效时，优先转专业系统。",
		notOtherReason: "轻训练产品不应承接治疗和危机干预责任。",
		betterAlternative: "如果没有即时风险但持续严重，建议预约心理咨询或精神科评估。",
	},
};

export const questions: Question[] = [
	{
		id: "entry",
		title: "你现在最想改善什么？",
		helper: "可以选 1-3 个。这里不是让你硬选领域，而是用用户语言做第一轮分流。",
		type: "multi",
		maxSelect: 3,
		priority: 100,
		options: [
			{ id: "sleep", label: "睡不好", domainScores: { sleep: 2 } },
			{
				id: "stress",
				label: "压力大 / 焦虑",
				domainScores: { stress: 2 },
				situationScores: { stress_anxiety: 1, stress_overload: 1 },
			},
			{
				id: "action",
				label: "拖延 / 没动力",
				domainScores: { action: 2 },
				situationScores: { action_procrastination: 1, action_low_motivation: 1 },
			},
			{
				id: "low-mood",
				label: "情绪低落",
				domainScores: { stress: 1, positive: 1 },
				situationScores: { low_mood: 2, positive_low_joy: 1 },
			},
			{
				id: "relationship",
				label: "和别人相处很累",
				domainScores: { relationship: 2 },
				situationScores: { relationship_conflict: 1, relationship_lonely: 1 },
			},
			{
				id: "body",
				label: "身体很疲惫",
				domainScores: { body: 2 },
				situationScores: { body_fatigue: 2, sleep_tired: 1 },
			},
			{
				id: "order",
				label: "生活很乱 / 手机停不下",
				domainScores: { order: 2 },
				situationScores: { order_chaos: 1, phone_overuse: 2, sleep_phone: 1 },
			},
			{
				id: "positive",
				label: "想让生活积极一点",
				domainScores: { positive: 2 },
				situationScores: { positive_low_joy: 2 },
			},
		],
	},
	{
		id: "sleep-primary",
		title: "睡眠里最困扰你的是哪一种？",
		helper: "这一题直接给睡眠下的具体情况加分。",
		type: "single",
		priority: 90,
		targetDomains: ["sleep"],
		shouldAsk: (state) => scoreOf(state.domainScores, "sleep") >= 2,
		options: [
			{
				id: "onset",
				label: "很久睡不着",
				description: "躺下后过很久还没睡着",
				situationScores: { sleep_onset: 3 },
			},
			{
				id: "rumination",
				label: "睡前想太多",
				description: "脑子停不下来",
				situationScores: { sleep_rumination: 3, stress_anxiety: 1 },
			},
			{
				id: "wake",
				label: "半夜容易醒 / 睡得浅",
				situationScores: { sleep_nightwake: 3 },
			},
			{
				id: "tired",
				label: "醒来还是累",
				situationScores: { sleep_tired: 3, body_fatigue: 1 },
			},
			{
				id: "rhythm",
				label: "睡觉起床时间很乱",
				situationScores: { sleep_rhythm: 3, order_chaos: 1 },
			},
			{
				id: "phone",
				label: "睡前刷手机停不下",
				situationScores: { sleep_phone: 3, phone_overuse: 1 },
			},
		],
	},
	{
		id: "sleep-latency",
		title: "最近入睡通常需要多久？",
		helper: "这题用来确认或排除“入睡难”。",
		type: "single",
		priority: 80,
		targetSituations: ["sleep_onset"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "sleep") >= 2 && !state.excludedSituations.has("sleep_onset"),
		options: [
			{
				id: "under-15",
				label: "15 分钟内",
				description: "基本能很快睡着",
				situationScores: { sleep_onset: -2 },
				excludeSituations: ["sleep_onset"],
			},
			{ id: "15-30", label: "15-30 分钟", situationScores: { sleep_onset: 1 } },
			{ id: "30-60", label: "30-60 分钟", situationScores: { sleep_onset: 2 } },
			{ id: "over-60", label: "超过 60 分钟", situationScores: { sleep_onset: 3 } },
		],
	},
	{
		id: "sleep-state",
		title: "躺下后，你更像哪种状态？",
		helper: "同样是睡不好，背后的练习会完全不同。",
		type: "single",
		priority: 78,
		targetDomains: ["sleep"],
		shouldAsk: (state) => scoreOf(state.domainScores, "sleep") >= 2,
		options: [
			{
				id: "body-tight",
				label: "身体紧绷 / 心跳快",
				situationScores: { body_tension: 2, sleep_onset: 1, panic_signal: 1 },
			},
			{
				id: "mind-racing",
				label: "脑子停不下来",
				situationScores: { sleep_rumination: 2, stress_anxiety: 1 },
			},
			{
				id: "phone",
				label: "刷手机停不下",
				situationScores: { sleep_phone: 2, phone_overuse: 2 },
			},
			{
				id: "not-sleepy",
				label: "就是不困",
				situationScores: { sleep_rhythm: 2, sleep_tired: 1 },
			},
		],
	},
	{
		id: "sleep-nightwake",
		title: "夜里会醒吗？醒后能不能继续睡？",
		helper: "用于判断是不是“夜醒 / 浅睡”，如果不是就会排除掉。",
		type: "single",
		priority: 70,
		targetSituations: ["sleep_nightwake"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "sleep") >= 2 &&
			(scoreOf(state.situationScores, "sleep_nightwake") > 0 || answeredCount(state) < 5),
		options: [
			{
				id: "rare",
				label: "很少醒",
				situationScores: { sleep_nightwake: -1 },
				excludeSituations: ["sleep_nightwake"],
			},
			{ id: "sometimes", label: "偶尔醒，但能睡回去", situationScores: { sleep_nightwake: 1 } },
			{ id: "often", label: "经常醒", situationScores: { sleep_nightwake: 3 } },
			{
				id: "hard-back",
				label: "醒后很难再睡",
				situationScores: { sleep_nightwake: 3, stress_anxiety: 1 },
			},
		],
	},
	{
		id: "sleep-rhythm",
		title: "最近 7 天，睡觉和起床时间稳定吗？",
		helper: "用来判断作息节律，而不是只看睡眠时长。",
		type: "single",
		priority: 68,
		targetSituations: ["sleep_rhythm", "sleep_phone"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "sleep") >= 2 || scoreOf(state.domainScores, "order") >= 2,
		options: [
			{
				id: "stable",
				label: "基本稳定",
				situationScores: { sleep_rhythm: -2 },
				excludeSituations: ["sleep_rhythm"],
			},
			{ id: "sometimes", label: "偶尔乱", situationScores: { sleep_rhythm: 1 } },
			{
				id: "two-hours",
				label: "经常相差 2 小时以上",
				situationScores: { sleep_rhythm: 3, order_chaos: 1 },
			},
			{
				id: "phone-night",
				label: "主要被手机拖晚",
				situationScores: { sleep_phone: 2, phone_overuse: 2, sleep_rhythm: 1 },
			},
		],
	},
	{
		id: "order-primary",
		title: "生活秩序里最困扰你的是哪一块？",
		helper: "生活乱不等于睡眠乱，先区分手机、空间、计划和作息。",
		type: "single",
		priority: 87,
		targetDomains: ["order"],
		shouldAsk: (state) => scoreOf(state.domainScores, "order") >= 2,
		options: [
			{
				id: "phone",
				label: "手机停不下",
				situationScores: { phone_overuse: 3, sleep_phone: 1 },
			},
			{ id: "space", label: "空间很乱", situationScores: { order_chaos: 3 } },
			{
				id: "plan",
				label: "计划总是失控",
				situationScores: { order_chaos: 2, stress_overload: 1 },
			},
			{
				id: "rhythm",
				label: "作息时间很乱",
				situationScores: { sleep_rhythm: 2, order_chaos: 1 },
			},
		],
	},
	{
		id: "phone-trigger",
		title: "你通常在什么情况下最容易刷手机停不下？",
		helper: "同样是手机问题，可能是在逃避任务、睡前放松、孤独，或者纯粹习惯自动化。",
		type: "single",
		priority: 60,
		targetSituations: ["phone_overuse", "sleep_phone"],
		shouldAsk: (state) =>
			scoreOf(state.situationScores, "phone_overuse") >= 2 ||
			scoreOf(state.situationScores, "sleep_phone") >= 2,
		options: [
			{ id: "bedtime", label: "睡前最严重", situationScores: { sleep_phone: 2, sleep_rhythm: 1 } },
			{
				id: "avoid-task",
				label: "不想面对任务时",
				situationScores: { action_procrastination: 2, phone_overuse: 1 },
			},
			{
				id: "lonely",
				label: "无聊或孤独时",
				situationScores: { relationship_lonely: 1, phone_overuse: 1 },
			},
			{ id: "habit", label: "没想太多，手会自动打开", situationScores: { phone_overuse: 2 } },
		],
	},
	{
		id: "stress-kind",
		title: "压力或情绪最像哪一种？",
		helper: "如果入口命中压力，这题决定是焦虑、过载、烦躁还是低落。",
		type: "single",
		priority: 86,
		targetDomains: ["stress"],
		shouldAsk: (state) => scoreOf(state.domainScores, "stress") >= 2,
		options: [
			{ id: "worry", label: "反复担心未来或结果", situationScores: { stress_anxiety: 3 } },
			{ id: "too-much", label: "事情太多，脑子很满", situationScores: { stress_overload: 3 } },
			{ id: "angry", label: "容易烦躁、想发火", situationScores: { stress_irritable: 3 } },
			{ id: "low", label: "低落，没什么开心感", situationScores: { low_mood: 3 } },
			{
				id: "panic",
				label: "心跳、胸闷、强烈害怕",
				situationScores: { panic_signal: 3 },
			},
		],
	},
	{
		id: "stress-intensity",
		title: "这个状态现在有多强？",
		helper: "强度会影响推荐普通练习还是先做稳定练习。",
		type: "single",
		priority: 74,
		targetDomains: ["stress"],
		shouldAsk: (state) => scoreOf(state.domainScores, "stress") >= 2,
		options: [
			{ id: "low", label: "1-2 分：有一点，但能处理" },
			{ id: "mid", label: "3 分：明显影响我", situationScores: { stress_overload: 1 } },
			{
				id: "high",
				label: "4 分：很强，难以停下来",
				situationScores: { stress_anxiety: 1, stress_overload: 1, panic_signal: 1 },
			},
			{
				id: "max",
				label: "5 分：快失控了",
				domainScores: { safety: 1 },
				situationScores: { panic_signal: 2, safety_risk: 1 },
			},
		],
	},
	{
		id: "low-mood-duration",
		title: "这种低落大概持续多久了？",
		helper: "低落不能只靠入口判断，持续时间会影响是普通状态波动，还是需要更谨慎处理。",
		type: "single",
		priority: 88,
		targetSituations: ["low_mood", "positive_low_joy"],
		shouldAsk: (state) => scoreOf(state.situationScores, "low_mood") >= 2,
		options: [
			{ id: "today", label: "今天或偶尔出现", situationScores: { low_mood: 1 } },
			{ id: "three-days", label: "最近 3 天左右", situationScores: { low_mood: 2 } },
			{
				id: "one-week",
				label: "一周以上",
				situationScores: { low_mood: 3, positive_low_joy: 1 },
			},
			{
				id: "two-weeks",
				label: "两周以上",
				domainScores: { safety: 1 },
				situationScores: { low_mood: 4, positive_low_joy: 1 },
			},
		],
	},
	{
		id: "low-mood-impact",
		title: "低落对你现在的影响有多大？",
		helper: "这一题用于区分轻微情绪低落、兴趣下降、功能受影响和风险信号。",
		type: "single",
		priority: 84,
		targetSituations: ["low_mood", "positive_low_joy", "safety_risk"],
		shouldAsk: (state) => scoreOf(state.situationScores, "low_mood") >= 2,
		options: [
			{ id: "mild", label: "有点低落，但还能正常做事", situationScores: { low_mood: 1 } },
			{
				id: "less-joy",
				label: "开心感明显变少，不太想做事",
				situationScores: { low_mood: 2, positive_low_joy: 2, action_low_motivation: 1 },
			},
			{
				id: "function",
				label: "明显影响睡眠、吃饭、工作或学习",
				situationScores: { low_mood: 3, body_fatigue: 1 },
			},
			{
				id: "hopeless",
				label: "强烈绝望，感觉快撑不住",
				domainScores: { safety: 3 },
				situationScores: { low_mood: 3, safety_risk: 4 },
			},
		],
	},
	{
		id: "low-mood-shape",
		title: "这个低落更像哪一种？",
		helper: "同样是低落，可能优先做积极体验、身体启动、关系连接或自我慈悲。",
		type: "single",
		priority: 58,
		targetSituations: ["low_mood", "positive_low_joy"],
		shouldAsk: (state) => scoreOf(state.situationScores, "low_mood") >= 2,
		options: [
			{
				id: "no-joy",
				label: "没什么开心感，也没有期待",
				domainScores: { positive: 1 },
				situationScores: { positive_low_joy: 3, low_mood: 1 },
			},
			{
				id: "tired",
				label: "身体没电，什么都不想动",
				domainScores: { body: 1 },
				situationScores: { body_fatigue: 2, action_low_motivation: 1 },
			},
			{
				id: "lonely",
				label: "孤独，觉得没人能理解",
				domainScores: { relationship: 1 },
				situationScores: { relationship_lonely: 2, low_mood: 1 },
			},
			{
				id: "self-blame",
				label: "容易自责，觉得自己不够好",
				domainScores: { self: 1 },
				situationScores: { self_criticism: 2, low_mood: 1 },
			},
		],
	},
	{
		id: "positive-primary",
		title: "你说“想积极一点”，更像是在改善什么？",
		helper: "积极生活不是默认低落，要区分缺少愉悦、缺少期待、缺少感恩关注，还是想建立积极习惯。",
		type: "single",
		priority: 86,
		targetDomains: ["positive"],
		targetSituations: ["positive_low_joy"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "positive") >= 2 &&
			scoreOf(state.situationScores, "low_mood") < 2,
		options: [
			{ id: "joy", label: "生活里开心的小事太少", situationScores: { positive_low_joy: 3 } },
			{ id: "expect", label: "没有什么期待", situationScores: { positive_low_joy: 2 } },
			{
				id: "gratitude",
				label: "很少注意到值得感谢的事",
				situationScores: { positive_low_joy: 2 },
			},
			{
				id: "habit",
				label: "想建立更积极的日常习惯",
				domainScores: { order: 1 },
				situationScores: { positive_low_joy: 1 },
			},
		],
	},
	{
		id: "positive-frequency",
		title: "过去一周，你有几天记录到舒服、开心或有意义的小事？",
		helper: "这题判断是不是缺少正向体验，也决定练习从感恩、成就还是关系连接开始。",
		type: "single",
		priority: 52,
		targetDomains: ["positive"],
		targetSituations: ["positive_low_joy"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "positive") >= 2 ||
			scoreOf(state.situationScores, "positive_low_joy") >= 2,
		options: [
			{
				id: "none",
				label: "几乎没有",
				situationScores: { positive_low_joy: 3, low_mood: 1 },
			},
			{ id: "one-two", label: "1-2 天", situationScores: { positive_low_joy: 2 } },
			{ id: "some", label: "3-4 天", situationScores: { positive_low_joy: 1 } },
			{
				id: "many",
				label: "5 天以上",
				situationScores: { positive_low_joy: -1 },
				excludeSituations: ["positive_low_joy"],
			},
		],
	},
	{
		id: "action-block",
		title: "你现在最卡住的是哪一步？",
		helper: "行动问题不一定是懒，可能是第一步、能量、压力或怕做不好。",
		type: "single",
		priority: 82,
		targetDomains: ["action"],
		shouldAsk: (state) => scoreOf(state.domainScores, "action") >= 2,
		options: [
			{ id: "unclear", label: "不知道第一步是什么", situationScores: { action_unclear_step: 3 } },
			{
				id: "avoid",
				label: "知道要做，但一直躲开",
				situationScores: { action_procrastination: 3, stress_anxiety: 1 },
			},
			{
				id: "no-energy",
				label: "没力气开始",
				situationScores: { action_low_motivation: 3, body_fatigue: 1 },
			},
			{
				id: "not-good",
				label: "怕做不好或被评价",
				situationScores: { perfection_block: 3, self_criticism: 1 },
			},
		],
	},
	{
		id: "action-impact",
		title: "这个行动卡住对你影响最大的是？",
		helper: "用于确认行动问题是低动力、压力过载、拖延回避，还是标准太高。",
		type: "single",
		priority: 57,
		targetDomains: ["action"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "action") >= 2 ||
			scoreOf(state.situationScores, "action_procrastination") >= 2 ||
			scoreOf(state.situationScores, "action_low_motivation") >= 2,
		options: [
			{
				id: "delay",
				label: "越拖越焦虑",
				situationScores: { action_procrastination: 2, stress_anxiety: 1 },
			},
			{
				id: "energy",
				label: "每天能量都不够",
				situationScores: { action_low_motivation: 2, body_fatigue: 1 },
			},
			{
				id: "messy",
				label: "任务太多，不知道先做哪个",
				situationScores: { action_unclear_step: 1, stress_overload: 2 },
			},
			{
				id: "standard",
				label: "总觉得现在做还不够好",
				situationScores: { perfection_block: 2, self_criticism: 1 },
			},
		],
	},
	{
		id: "self-kind",
		title: "最近你对自己的态度更像哪种？",
		helper: "用来识别自责、低自信和完美主义是否在加重问题。",
		type: "single",
		priority: 62,
		targetDomains: ["self"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "self") >= 2 ||
			scoreOf(state.situationScores, "self_criticism") > 0 ||
			scoreOf(state.situationScores, "perfection_block") >= 2,
		options: [
			{
				id: "criticize",
				label: "总觉得自己不够好",
				situationScores: { self_criticism: 3 },
			},
			{
				id: "confidence",
				label: "不太相信自己能做到",
				situationScores: { self_low_confidence: 3 },
			},
			{
				id: "perfection",
				label: "标准很高，不做到好就不想做",
				situationScores: { perfection_block: 2, self_criticism: 1 },
			},
			{ id: "not-main", label: "这不是主要问题", situationScores: { self_criticism: -1 } },
		],
	},
	{
		id: "relationship-kind",
		title: "人际关系里最消耗你的是？",
		helper: "如果关系命中，会区分孤独断联还是冲突边界。",
		type: "single",
		priority: 72,
		targetDomains: ["relationship"],
		shouldAsk: (state) => scoreOf(state.domainScores, "relationship") >= 2,
		options: [
			{
				id: "lonely",
				label: "觉得孤独，没人能说",
				situationScores: { relationship_lonely: 3, low_mood: 1 },
			},
			{
				id: "conflict",
				label: "和别人冲突后很难受",
				situationScores: { relationship_conflict: 3 },
			},
			{
				id: "boundary",
				label: "不敢拒绝或表达需求",
				situationScores: { relationship_conflict: 2, self_low_confidence: 1 },
			},
			{ id: "not-main", label: "不是主要问题", domainScores: { relationship: -1 } },
		],
	},
	{
		id: "relationship-need",
		title: "这段关系困扰里，你现在最需要什么？",
		helper: "用来决定推荐连接练习、边界表达，还是先做情绪整理。",
		type: "single",
		priority: 56,
		targetDomains: ["relationship"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "relationship") >= 2 ||
			scoreOf(state.situationScores, "relationship_lonely") >= 2 ||
			scoreOf(state.situationScores, "relationship_conflict") >= 2,
		options: [
			{
				id: "connect",
				label: "想和一个人重新连接",
				situationScores: { relationship_lonely: 2 },
			},
			{
				id: "say-need",
				label: "想把需求说清楚",
				situationScores: { relationship_conflict: 1, self_low_confidence: 1 },
			},
			{
				id: "boundary",
				label: "想拒绝或拉开边界",
				situationScores: { relationship_conflict: 2 },
			},
			{
				id: "calm",
				label: "现在先想让自己平静下来",
				situationScores: { stress_irritable: 1, low_mood: 1 },
			},
		],
	},
	{
		id: "body-energy",
		title: "身体层面最明显的是？",
		helper: "身体能量会决定是先动起来，还是先放松下来。",
		type: "single",
		priority: 66,
		targetDomains: ["body"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "body") >= 2 ||
			scoreOf(state.situationScores, "body_fatigue") >= 2 ||
			scoreOf(state.situationScores, "body_tension") >= 2,
		options: [
			{ id: "tired", label: "疲惫，没电", situationScores: { body_fatigue: 3 } },
			{ id: "tight", label: "紧绷、肩颈或胸口不舒服", situationScores: { body_tension: 3 } },
			{ id: "care", label: "基础照顾断了，比如喝水吃饭洗漱", situationScores: { body_fatigue: 2 } },
			{ id: "ok", label: "身体不是主要问题", domainScores: { body: -1 } },
		],
	},
	{
		id: "body-basics",
		title: "最近基础身体照顾里，哪一个最容易断掉？",
		helper: "身体能量低时，练习要从最小照顾动作开始，而不是直接上复杂课程。",
		type: "single",
		priority: 54,
		targetDomains: ["body"],
		shouldAsk: (state) =>
			scoreOf(state.domainScores, "body") >= 2 ||
			scoreOf(state.situationScores, "body_fatigue") >= 2 ||
			scoreOf(state.situationScores, "body_tension") >= 2,
		options: [
			{ id: "water", label: "喝水少", situationScores: { body_fatigue: 1 } },
			{ id: "movement", label: "几乎不动", situationScores: { body_fatigue: 1, body_tension: 1 } },
			{ id: "tight", label: "肩颈和身体紧绷", situationScores: { body_tension: 2 } },
			{
				id: "sleep",
				label: "睡眠恢复差",
				domainScores: { sleep: 1 },
				situationScores: { sleep_tired: 2 },
			},
		],
	},
	{
		id: "safety-check",
		title: "最近有没有出现伤害自己、失控或不想活的念头？",
		helper: "这题只在强低落、强警报或风险信号时出现。出现风险时优先推荐真人支持。",
		type: "single",
		priority: 95,
		targetDomains: ["safety"],
		shouldAsk: (state) =>
			scoreOf(state.situationScores, "low_mood") >= 5 ||
			scoreOf(state.situationScores, "panic_signal") >= 3 ||
			scoreOf(state.situationScores, "safety_risk") > 0,
		options: [
			{ id: "no", label: "没有" },
			{
				id: "brief",
				label: "偶尔闪过，但没有计划",
				domainScores: { safety: 2 },
				situationScores: { safety_risk: 3 },
			},
			{
				id: "clear",
				label: "有明确想法或担心自己会失控",
				domainScores: { safety: 4 },
				situationScores: { safety_risk: 5 },
			},
		],
	},
	{
		id: "general-impact",
		title: "整体来看，这个状态现在对你的影响有多大？",
		helper:
			"这是兜底确认题：如果前面的答案已经缩小范围，但还不到最低判断题数，会用它补足影响程度。",
		type: "single",
		priority: 5,
		shouldAsk: (state) => answeredCount(state) > 0,
		options: [
			{ id: "small", label: "影响不大，只是想调整一下" },
			{ id: "medium", label: "有明显影响，但还能处理", situationScores: { stress_overload: 1 } },
			{
				id: "large",
				label: "影响睡眠、工作、学习或关系",
				domainScores: { stress: 1 },
				situationScores: { stress_overload: 1, body_fatigue: 1 },
			},
			{
				id: "risk",
				label: "很强烈，担心自己撑不住",
				domainScores: { safety: 2 },
				situationScores: { safety_risk: 3 },
			},
		],
	},
];

const emptyDomainScores = (): Record<DomainId, number> =>
	Object.keys(domains).reduce(
		(acc, id) => {
			acc[id as DomainId] = 0;
			return acc;
		},
		{} as Record<DomainId, number>,
	);

const emptySituationScores = (): Record<SituationId, number> =>
	Object.keys(situations).reduce(
		(acc, id) => {
			acc[id as SituationId] = 0;
			return acc;
		},
		{} as Record<SituationId, number>,
	);

const initialState = (): AssessmentState => ({
	answers: [],
	domainScores: emptyDomainScores(),
	situationScores: emptySituationScores(),
	excludedSituations: new Set<SituationId>(),
});

export const AssessmentPlayground: React.FC = () => {
	const [state, setState] = useState<AssessmentState>(() => initialState());
	const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
	const [phase, setPhase] = useState<"question" | "result">("question");

	const result = useMemo(() => buildResult(state), [state]);
	const currentQuestion = useMemo(
		() => (phase === "question" ? pickNextQuestion(state) : undefined),
		[state, phase],
	);
	const canFinish = shouldFinish(state);

	const selectedOptions = currentQuestion
		? currentQuestion.options.filter((option) => selectedOptionIds.includes(option.id))
		: [];

	const toggleOption = (optionId: string) => {
		if (!currentQuestion) {
			return;
		}

		if (currentQuestion.type === "single") {
			setSelectedOptionIds([optionId]);
			return;
		}

		setSelectedOptionIds((current) => {
			if (current.includes(optionId)) {
				return current.filter((id) => id !== optionId);
			}
			const max = currentQuestion.maxSelect ?? currentQuestion.options.length;
			if (current.length >= max) {
				return [...current.slice(1), optionId];
			}
			return [...current, optionId];
		});
	};

	const submitAnswer = () => {
		if (!currentQuestion || selectedOptions.length === 0) {
			return;
		}

		setState((current) => {
			const nextState = applyAnswer(current, currentQuestion, selectedOptions);
			if (
				shouldFinish(nextState) ||
				(!pickNextQuestion(nextState) && answeredCount(nextState) >= MIN_QUESTIONS)
			) {
				window.setTimeout(() => setPhase("result"), 0);
			}
			return nextState;
		});
		setSelectedOptionIds([]);
	};

	const reset = () => {
		setState(initialState());
		setSelectedOptionIds([]);
		setPhase("question");
	};

	return (
		<div className="space-y-6">
			<section className="grid gap-4 lg:grid-cols-[1.25fr_0.75fr]">
				<Card className="overflow-hidden rounded-2xl">
					<CardHeader className="border-b border-border/70 bg-muted/35">
						<div className="flex flex-wrap items-center gap-3">
							<Badge className="bg-primary text-primary-foreground">前台体验</Badge>
							<Badge variant="outline">最少 {MIN_QUESTIONS} 题</Badge>
							<Badge variant="outline">最多 {MAX_QUESTIONS} 题</Badge>
							<Badge variant="outline">
								已答 {state.answers.length} / {MAX_QUESTIONS}
							</Badge>
						</div>
						<CardTitle className="text-3xl">自适应状态识别体验</CardTitle>
						<CardDescription className="max-w-3xl text-base">
							每个答案都会给领域和具体情况加分，也可能排除一部分情况。系统判断足够清楚时会提前结束，并给出得分、分析和练习动作。
						</CardDescription>
					</CardHeader>
					<CardContent className="p-6">
						{phase === "question" && currentQuestion ? (
							<QuestionPanel
								canFinish={canFinish}
								onFinish={() => setPhase("result")}
								onSubmit={submitAnswer}
								question={currentQuestion}
								selectedOptionIds={selectedOptionIds}
								toggleOption={toggleOption}
							/>
						) : (
							<ResultPanel onReset={reset} result={result} state={state} />
						)}
					</CardContent>
				</Card>

				<div className="space-y-4">
					<ScoreCard
						description="入口题先给领域加分，后续题会继续加分或降分。"
						items={result.topDomains.map(({ id, score }) => ({
							id,
							name: domains[id].name,
							score,
							description: domains[id].description,
						}))}
						title="领域得分"
					/>
					<ScoreCard
						description="最终推荐不是看领域，而是看具体情况的分数。"
						items={result.topSituations.map(({ id, score }) => ({
							id,
							name: situations[id].name,
							score,
							description: situations[id].description,
						}))}
						title="具体情况得分"
					/>
				</div>
			</section>

			<Card className="rounded-2xl">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-xl">
						<ListChecks className="size-5 text-primary" />
						答题轨迹
					</CardTitle>
					<CardDescription>
						这里能看到每道题如何改变判断。后面做后台配置时，题目就应该配置这些加分规则。
					</CardDescription>
				</CardHeader>
				<CardContent>
					{state.answers.length === 0 ? (
						<p className="text-sm text-muted-foreground">还没有答题。</p>
					) : (
						<div className="grid gap-3">
							{state.answers.map((answer, index) => (
								<div
									className="rounded-xl border border-border/70 bg-background/70 p-4"
									key={`${answer.questionId}-${index}`}
								>
									<p className="text-sm font-semibold text-foreground">
										{index + 1}. {answer.questionTitle}
									</p>
									<div className="mt-2 flex flex-wrap gap-2">
										{answer.options.map((option) => (
											<Badge key={option.id} variant="secondary">
												{option.label}
											</Badge>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

function QuestionPanel({
	canFinish,
	onFinish,
	onSubmit,
	question,
	selectedOptionIds,
	toggleOption,
}: {
	canFinish: boolean;
	onFinish: () => void;
	onSubmit: () => void;
	question: Question;
	selectedOptionIds: string[];
	toggleOption: (optionId: string) => void;
}) {
	return (
		<div className="grid gap-5">
			<div className="rounded-2xl border border-primary/25 bg-primary/5 p-5">
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="min-w-0">
						<p className="text-sm font-medium text-primary">当前问题</p>
						<h2 className="mt-2 text-2xl font-semibold text-foreground">{question.title}</h2>
						<p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
							{question.helper}
						</p>
					</div>
					<Badge variant="outline">{question.type === "multi" ? "可多选" : "单选"}</Badge>
				</div>
			</div>

			<div className="grid gap-3 md:grid-cols-2">
				{question.options.map((option) => {
					const selected = selectedOptionIds.includes(option.id);
					return (
						<button
							className={cn(
								"min-h-24 rounded-2xl border p-4 text-left transition-colors",
								selected
									? "border-primary bg-primary/10 shadow-sm"
									: "border-border/80 bg-card hover:border-primary/55 hover:bg-primary/5",
							)}
							key={option.id}
							onClick={() => toggleOption(option.id)}
							type="button"
						>
							<div className="flex items-start justify-between gap-3">
								<div>
									<p className="text-base font-semibold text-foreground">{option.label}</p>
									{option.description ? (
										<p className="mt-1 text-sm leading-6 text-muted-foreground">
											{option.description}
										</p>
									) : null}
								</div>
								<span
									className={cn(
										"flex size-7 items-center justify-center rounded-full border",
										selected
											? "border-primary bg-primary text-primary-foreground"
											: "border-border bg-background text-transparent",
									)}
								>
									<CheckCircle2 className="size-4" />
								</span>
							</div>
						</button>
					);
				})}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-5">
				<div className="text-sm text-muted-foreground">
					{canFinish
						? "当前信息已经足够形成一个初步判断，可以直接看结果。"
						: "继续答题会缩小判断范围。"}
				</div>
				<div className="flex gap-3">
					{canFinish ? (
						<Button onClick={onFinish} type="button" variant="outline">
							查看当前判断
						</Button>
					) : null}
					<Button disabled={selectedOptionIds.length === 0} onClick={onSubmit} type="button">
						<span>下一题</span>
						<ArrowRight />
					</Button>
				</div>
			</div>
		</div>
	);
}

function ResultPanel({
	onReset,
	result,
	state,
}: {
	onReset: () => void;
	result: ReturnType<typeof buildResult>;
	state: AssessmentState;
}) {
	const main = result.topSituations[0];
	const companions = result.topSituations.slice(1, 4).filter((item) => item.score > 0);
	const isSafety = main?.id === "safety_risk";

	return (
		<div className="grid gap-5">
			<div
				className={cn(
					"rounded-2xl border p-5",
					isSafety ? "border-destructive/30 bg-destructive/10" : "border-primary/25 bg-primary/5",
				)}
			>
				<div className="flex flex-wrap items-center gap-3">
					{isSafety ? (
						<ShieldAlert className="size-6 text-destructive" />
					) : (
						<Target className="size-6 text-primary" />
					)}
					<Badge variant={isSafety ? "destructive" : "default"}>
						一共答了 {state.answers.length} 题
					</Badge>
					<Badge variant="outline">上限 {MAX_QUESTIONS} 题</Badge>
				</div>
				<h2 className="mt-4 text-3xl font-semibold text-foreground">
					{main ? `主判断：${situations[main.id].name}` : "还没有足够判断"}
				</h2>
				<p className="mt-3 max-w-3xl leading-7 text-muted-foreground">
					{main
						? situations[main.id].description
						: `建议至少回答 ${MIN_QUESTIONS} 个问题，再形成初步判断。`}
				</p>
				{companions.length > 0 ? (
					<div className="mt-4 flex flex-wrap gap-2">
						<span className="text-sm text-muted-foreground">伴随状态</span>
						{companions.map((item) => (
							<Badge key={item.id} variant="secondary">
								{situations[item.id].name} · {item.score}
							</Badge>
						))}
					</div>
				) : null}
			</div>

			<div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
				<Card className="rounded-2xl shadow-none">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-xl">
							<BarChart3 className="size-5 text-primary" />
							为什么这么判断
						</CardTitle>
						<CardDescription>这里只展示命中主判断和伴随判断的关键证据。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						{result.evidence.length === 0 ? (
							<p className="text-sm text-muted-foreground">暂无证据。</p>
						) : (
							result.evidence.slice(0, 8).map((item) => (
								<div
									className="rounded-xl border border-border/70 bg-background/80 p-3"
									key={`${item.label}-${item.target}`}
								>
									<p className="text-sm font-medium text-foreground">{item.label}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										命中：{item.target} +{item.score}
									</p>
								</div>
							))
						)}
					</CardContent>
				</Card>

				<Card className="rounded-2xl shadow-none">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-xl">
							<Sparkles className="size-5 text-primary" />
							推荐练习动作
						</CardTitle>
						<CardDescription>
							前台先给 1 个主练习和 2-3 个备选。用户反馈不好，再换练习。
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-3">
							{result.practices.map((practice, index) => (
								<div
									className={cn(
										"rounded-2xl border p-4",
										index === 0
											? "border-primary/35 bg-primary/8"
											: "border-border/70 bg-background/80",
									)}
									key={practice.title}
								>
									<div className="flex flex-wrap items-center gap-2">
										{index === 0 ? <Badge>主练习</Badge> : <Badge variant="outline">备选</Badge>}
										<Badge variant="secondary">{practice.mode}</Badge>
										<Badge variant="outline">{practice.category}</Badge>
										<Badge variant="outline">积极心理学：{practice.positiveFit}</Badge>
										<Badge variant="secondary">证据：{practice.evidenceLevel}</Badge>
									</div>
									<h3 className="mt-3 text-lg font-semibold text-foreground">{practice.title}</h3>
									<p className="mt-1 text-sm text-muted-foreground">{practice.duration}</p>
									<p className="mt-3 text-sm leading-6 text-muted-foreground">{practice.reason}</p>
									<div className="mt-4 grid gap-3 text-sm">
										<PracticeExplanation label="理论来源" value={practice.theory} />
										<PracticeExplanation label="为什么推荐它" value={practice.selectionReason} />
										<PracticeExplanation label="为什么不是别的" value={practice.notOtherReason} />
										<PracticeExplanation
											label="什么时候有更好替代"
											value={practice.betterAlternative}
										/>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</div>

			<div className="flex justify-end">
				<Button onClick={onReset} type="button" variant="outline">
					<RotateCcw />
					重新体验
				</Button>
			</div>
		</div>
	);
}

function PracticeExplanation({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-border/60 bg-background/75 p-3">
			<p className="text-xs font-semibold text-muted-foreground">{label}</p>
			<p className="mt-1 leading-6 text-foreground">{value}</p>
		</div>
	);
}

function ScoreCard({
	description,
	items,
	title,
}: {
	description: string;
	items: { id: string; name: string; score: number; description: string }[];
	title: string;
}) {
	const maxScore = Math.max(1, ...items.map((item) => item.score));

	return (
		<Card className="rounded-2xl">
			<CardHeader>
				<CardTitle className="text-lg">{title}</CardTitle>
				<CardDescription>{description}</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{items.slice(0, 6).map((item) => (
					<div key={item.id}>
						<div className="flex items-center justify-between gap-3">
							<p className="text-sm font-medium text-foreground">{item.name}</p>
							<Badge variant={item.score > 0 ? "secondary" : "outline"}>{item.score}</Badge>
						</div>
						<div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
							<div
								className="h-full rounded-full bg-primary"
								style={{ width: `${Math.max(0, (item.score / maxScore) * 100)}%` }}
							/>
						</div>
						<p className="mt-1 text-xs leading-5 text-muted-foreground">{item.description}</p>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function applyAnswer(
	state: AssessmentState,
	question: Question,
	options: AnswerOption[],
): AssessmentState {
	const nextDomainScores = { ...state.domainScores };
	const nextSituationScores = { ...state.situationScores };
	const nextExcludedSituations = new Set(state.excludedSituations);

	for (const option of options) {
		for (const [id, score] of Object.entries(option.domainScores ?? {})) {
			nextDomainScores[id as DomainId] = Math.max(
				0,
				(nextDomainScores[id as DomainId] ?? 0) + (score ?? 0),
			);
		}

		for (const [id, score] of Object.entries(option.situationScores ?? {})) {
			nextSituationScores[id as SituationId] = Math.max(
				0,
				(nextSituationScores[id as SituationId] ?? 0) + (score ?? 0),
			);
		}

		for (const situationId of option.excludeSituations ?? []) {
			nextExcludedSituations.add(situationId);
			nextSituationScores[situationId] = 0;
		}
	}

	return {
		answers: [
			...state.answers,
			{
				questionId: question.id,
				questionTitle: question.title,
				options,
			},
		],
		domainScores: nextDomainScores,
		excludedSituations: nextExcludedSituations,
		situationScores: nextSituationScores,
	};
}

function pickNextQuestion(state: AssessmentState): Question | undefined {
	const answeredQuestionIds = new Set(state.answers.map((answer) => answer.questionId));

	if (!answeredQuestionIds.has("entry")) {
		return questions[0];
	}

	const candidates = questions.filter((question) => {
		if (answeredQuestionIds.has(question.id)) {
			return false;
		}
		if (question.shouldAsk && !question.shouldAsk(state)) {
			return false;
		}
		return true;
	});

	const topDomain = sortScores(state.domainScores)[0]?.id;
	const topSituation = sortSituationScores(state.situationScores)[0]?.id;

	return candidates.sort((a, b) => {
		const scoreA = questionRank(a, topDomain, topSituation);
		const scoreB = questionRank(b, topDomain, topSituation);
		return scoreB - scoreA;
	})[0];
}

function questionRank(
	question: Question,
	topDomain: DomainId | undefined,
	topSituation: SituationId | undefined,
) {
	let rank = question.priority;
	if (topDomain && question.targetDomains?.includes(topDomain)) {
		rank += 20;
	}
	if (topSituation && question.targetSituations?.includes(topSituation)) {
		rank += 25;
	}
	return rank;
}

function shouldFinish(state: AssessmentState) {
	const count = answeredCount(state);
	const topSituation = sortSituationScores(state.situationScores)[0];
	const topDomain = sortScores(state.domainScores)[0];
	const needsSafetyCheck =
		(scoreOf(state.situationScores, "low_mood") >= 5 ||
			scoreOf(state.situationScores, "panic_signal") >= 3 ||
			scoreOf(state.situationScores, "safety_risk") > 0) &&
		!hasAnswered(state, "safety-check");

	if (count >= MAX_QUESTIONS) {
		return true;
	}
	if (needsSafetyCheck) {
		return false;
	}
	if (scoreOf(state.situationScores, "safety_risk") >= 3) {
		return true;
	}
	if (!topSituation || !topDomain) {
		return false;
	}
	if (count < MIN_QUESTIONS) {
		return false;
	}
	if (topSituation.score >= 6) {
		return true;
	}
	if (topSituation.score >= 5 && topDomain.score >= 2) {
		return true;
	}
	if (count >= 6 && topSituation.score >= 3) {
		return true;
	}
	return false;
}

function buildResult(state: AssessmentState) {
	const topDomains = sortScores(state.domainScores);
	const topSituations = sortSituationScores(state.situationScores).filter(
		(item) => !state.excludedSituations.has(item.id),
	);
	const practiceIds = new Set<PracticeId>();

	for (const situation of topSituations.slice(0, 3)) {
		for (const practiceId of situations[situation.id].practiceIds) {
			practiceIds.add(practiceId);
		}
	}

	const evidence = buildEvidence(
		state,
		topSituations.slice(0, 4).map((item) => item.id),
	);

	return {
		evidence,
		practices: Array.from(practiceIds)
			.map((id) => practiceCatalog[id])
			.slice(0, 4),
		topDomains,
		topSituations,
	};
}

function buildEvidence(state: AssessmentState, targetSituationIds: SituationId[]) {
	const targetSet = new Set(targetSituationIds);
	const evidence: { label: string; target: string; score: number }[] = [];

	for (const answer of state.answers) {
		for (const option of answer.options) {
			for (const [situationId, score] of Object.entries(option.situationScores ?? {})) {
				if (!targetSet.has(situationId as SituationId) || !score || score <= 0) {
					continue;
				}
				evidence.push({
					label: `${answer.questionTitle}：${option.label}`,
					score,
					target: situations[situationId as SituationId].name,
				});
			}
		}
	}

	return evidence.sort((a, b) => b.score - a.score);
}

function sortScores<T extends string>(scores: Record<T, number>) {
	return Object.entries(scores)
		.map(([id, score]) => ({ id: id as T, score: Number(score) }))
		.sort((a, b) => b.score - a.score);
}

function sortSituationScores(scores: Record<SituationId, number>) {
	return sortScores(scores).sort((a, b) => {
		if (a.id === "safety_risk" && a.score >= 3) {
			return -1;
		}
		if (b.id === "safety_risk" && b.score >= 3) {
			return 1;
		}
		return b.score - a.score;
	});
}

function scoreOf<T extends string>(scores: Record<T, number>, id: T) {
	return scores[id] ?? 0;
}

function answeredCount(state: AssessmentState) {
	return state.answers.length;
}

function hasAnswered(state: AssessmentState, questionId: string) {
	return state.answers.some((answer) => answer.questionId === questionId);
}
